import type { ZodType } from "zod";
import z from "zod";

/**
 * A value a JSON document can hold.
 *
 * A submitted response is typed this widely on purpose: what a response may be
 * is the instrument's schema to say, not this type's. An instrument that
 * declares a free-text item, or a dated one, needs no change here and no
 * change to the column the value lands in — only a new schema.
 */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

/**
 * One answer. A comment on another answer is an answer of its own, under its
 * own key.
 */
export type SubmittedItemUnbranded = {
  response: JsonValue;
};

/**
 * What every source sends: responses grouped as the instrument groups them.
 *
 * Nested by group rather than flat, so a group being optional is the schema's
 * own `required` and a module arrives as one absent object rather than as a
 * rule of ours about which keys must appear together.
 */
export type SubmissionDocumentUnbranded = Readonly<
  Record<string, Readonly<Record<string, SubmittedItemUnbranded>>>
>;

export const JsonValueSchema: ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);

/**
 * The document's shape, and nothing about what a particular instrument asks.
 *
 * Two checks in two places, deliberately: this says the body is a document at
 * all, and the pinned instrument's own schema says whether it is a valid one.
 * Only the second knows which groups exist, which items are mandatory and what
 * each response may be.
 */
export const SubmissionDocumentSchema = z.record(
  z.string(),
  z.record(z.string(), z.object({ response: JsonValueSchema }).strict()),
) satisfies ZodType<SubmissionDocumentUnbranded>;

/**
 * One answer of a stored submission, flattened for the table, which calls it a
 * response (`docs/glossary/answer.md`).
 */
export type ResponseRow = {
  group: string;
  item: string;
  response: JsonValue;
};

/**
 * The document as rows, each response exactly as it was sent: a submission is
 * an attestation, and the core never alters what it attests.
 */
export const toResponseRows = (
  document: SubmissionDocumentUnbranded,
): ResponseRow[] =>
  Object.entries(document).flatMap(([group, items]) =>
    Object.entries(items).map(([item, { response }]) => ({
      group,
      item,
      response,
    })),
  );

/**
 * The document stored rows were flattened from, so a submission can be taken
 * up again as a draft. Undoes `toResponseRows` but for a group with nothing
 * answered, which flattening drops.
 */
export const documentOf = (
  rows: readonly ResponseRow[],
): SubmissionDocumentUnbranded => {
  const document: Record<string, Record<string, SubmittedItemUnbranded>> = {};

  for (const { group, item, response } of rows) {
    document[group] = { ...document[group], [item]: { response } };
  }

  return document;
};
